export const PASSWORD_RESET_UNCONFIRMED = 'Password reset could not be confirmed. Try signing in with the new password before requesting a new reset link.';

const statusMessages = {
  400: ['INVALID_REQUEST', 'Check the supplied information and try again.'],
  401: ['UNAUTHORIZED', 'Sign in to continue.'],
  403: ['FORBIDDEN', 'You do not have permission to perform this action.'],
  404: ['NOT_FOUND', 'The requested item is unavailable.'],
  409: ['CONFLICT', 'The saved information changed. Reload and review it before trying again.'],
  410: ['RETIRED', 'This action is no longer available.'],
  413: ['PAYLOAD_TOO_LARGE', 'The request is too large. Reduce its size and try again.'],
  415: ['UNSUPPORTED_MEDIA_TYPE', 'This file or request format is not supported.'],
  422: ['INVALID_REQUEST', 'Check the highlighted fields and try again.'],
  429: ['RATE_LIMITED', 'Too many requests. Wait before trying again.'],
  502: ['UPSTREAM_FAILURE', 'The service is temporarily unavailable. Your request could not be confirmed.'],
  503: ['SERVICE_UNAVAILABLE', 'The service is temporarily unavailable. Your request could not be confirmed.'],
  504: ['UPSTREAM_TIMEOUT', 'The service took too long to respond. Your request could not be confirmed.'],
};
const codeMessages = {
  ACCOUNT_LOCKED: 'Your account is temporarily locked. Wait before trying again or contact your institution.',
  ACCOUNT_UNAVAILABLE: 'Sign-in access is restricted. Contact your institution for help with your account.',
  EMAIL_CREDENTIALS_REJECTED: 'The email provider rejected its credentials. Ask the platform administrator to check the email configuration.',
  EMAIL_RECIPIENT_NOT_ALLOWED: 'The email provider did not allow this recipient. Ask the platform administrator to check the sending domain.',
  EMAIL_PROVIDER_REJECTED: 'The email provider rejected the message. Ask the platform administrator to check delivery settings.',
  EMAIL_PROVIDER_UNAVAILABLE: 'Email delivery could not be confirmed. Check the delivery records before retrying.',
  EMAIL_DELIVERY_UNCONFIRMED: 'Email delivery could not be confirmed. Check the delivery records before retrying.',
  STUDENT_SETUP_UNAVAILABLE: 'The setup link could not be confirmed. Check the saved account before retrying.',
  PASSWORD_RESET_FAILED: PASSWORD_RESET_UNCONFIRMED,
  ACCOUNT_CHANGED: 'Your account changed. Sign in again before continuing.',
  ACCESS_DENIED: statusMessages[403][1],
  AUTH_REQUIRED: statusMessages[401][1],
  AUTH_UNAVAILABLE: 'Account verification is temporarily unavailable. Try again shortly.',
  DUPLICATE_QUESTION: 'A question with similar content already exists.',
  EMAIL_NOT_CONFIGURED: 'Email delivery is unavailable. Check the saved account before retrying.',
  EMAIL_NOT_VERIFIED: 'Verify your email before signing in. Check your inbox and spam folder or contact your institution.',
  ELIGIBILITY_UNAVAILABLE: 'Access could not be checked. Try again shortly.',
  INVALID_CURRENT_PASSWORD: 'The current password is incorrect.',
  INVALID_CREDENTIALS: 'The email or password is incorrect.',
  INVALID_TOKEN: 'This link is invalid or expired. Request a new link.',
  INVALID_EXCEL_STRUCTURE: 'Check that the workbook contains the required headers.',
  PAPER_UNAVAILABLE: 'The saved test paper is unavailable. Contact your institution for help.',
  TENANT_CODE_EXISTS: 'This institution code is already in use. Choose a different one.',
  WEAK_PASSWORD: 'Choose a password that meets the listed requirements.',
};
const safeLegacyMessages = new Set([
  'Invalid email or password', 'Current password is incorrect',
  'Invalid or expired token', 'Invalid or expired setup token',
  'Membership changed; reload and retry', 'Membership changed; sign in again',
  'Invalid, expired or consumed reset token', 'Session revoked; sign in again',
  'Session progress changed during submission; retry submission',
  'Account setup is already complete.',
  'Required', 'Invalid', 'Too short', 'Invalid format',
  'Workbook has no question rows', 'Workbook could not be read',
  'Workbook is missing required headers: QTYPE, QTEXT, CORRECT_ANSWER, SUBJECT and DIFFICULTY_LEVEL.',
  'Workbook contains duplicate headers. Use each required header once.',
  'Use an Excel workbook up to 5MB', 'File is empty',
  'The welcome email could not be sent. Ask the administrator to check delivery logs before retrying.',
]);
const referencePattern = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/;
const codePattern = /^[A-Z][A-Z_0-9]{2,63}$/;
const fieldPattern = /^[A-Za-z][A-Za-z0-9_.\[\]-]{0,99}$/;
const remembered = new WeakMap();
const sessionChecks = new Map();

export async function confirmSessionRejection(token, readToken, endSession) {
  if (!token || readToken() !== token) return false;
  if (!sessionChecks.has(token)) {
    const check = (async () => {
      try {
        const response = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${token.replace(/^Bearer /, '')}` },
          signal: AbortSignal.timeout(7000),
        });
        if (![401, 403].includes(response.status) || readToken() !== token) return false;
        endSession(token);
        return true;
      } catch { return false; }
    })();
    sessionChecks.set(token, check);
    void check.finally(() => sessionChecks.delete(token));
  }
  return sessionChecks.get(token);
}

function readHeader(headers, name) {
  return (typeof headers?.get === 'function' ? headers.get(name) : undefined) ?? headers?.[name] ?? headers?.[name.toLowerCase()];
}

export function parseRetryAfter(value, now = Date.now()) {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  const text = String(value).trim();
  if (/^\d+$/.test(text)) {
    const seconds = Number(text);
    return Number.isSafeInteger(seconds) ? seconds : undefined;
  }
  if (!/^[A-Za-z]{3}, /.test(text)) return undefined;
  const date = Date.parse(text);
  return Number.isFinite(date) ? Math.max(0, Math.ceil((date - now) / 1000)) : undefined;
}

function safeText(value) {
  return typeof value === 'string' && value.length <= 500 && value.trim() && !/[<>\x00-\x1f]/.test(value);
}

function readFields(data, canonical) {
  const raw = data?.fieldErrors ?? data?.validationErrors ?? (data?.field ? { [data.field]: data.message } : null);
  const fields = {};
  const entries = Array.isArray(raw) ? raw.map(entry => [entry?.field, entry?.message]) : Object.entries(raw ?? {});
  for (const [field, values] of entries.slice(0, 100)) {
    if (typeof field !== 'string' || !fieldPattern.test(field) || Object.hasOwn(Object.prototype, field)) continue;
    const messages = (Array.isArray(values) ? values : [values]).slice(0, 5);
    fields[field] = messages.map(value => (canonical && safeText(value)) || safeLegacyMessages.has(value) ? value : 'Check this value.');
  }
  return fields;
}

export function normalizeApiError(error, options = {}) {
  const cached = error && typeof error === 'object' ? remembered.get(error) : undefined;
  if (cached && (cached.code === 'SESSION_CHECK_UNAVAILABLE' || (!options.fallback && !options.login))) return cached;
  const response = error?.response;
  const rawData = response?.data;
  const data = rawData?.failure && typeof rawData.failure === 'object' ? rawData.failure : rawData;
  const status = Number.isInteger(response?.status) ? response.status : undefined;
  const cancelled = error?.code === 'ERR_CANCELED' || error?.name === 'AbortError';
  const timeout = ['ECONNABORTED', 'ETIMEDOUT'].includes(error?.code) || error?.name === 'TimeoutError';
  const network = error?.code === 'ERR_NETWORK' || error?.message === 'Network Error' || (error?.isAxiosError && !response && !error.code);
  const kind = cancelled ? 'cancelled' : status ? 'http' : timeout ? 'timeout' : network ? 'network' : 'unexpected';
  const bodyReference = data?.requestId;
  const headerReference = readHeader(response?.headers, 'X-Request-ID');
  const requestId = [headerReference, bodyReference].find(value => typeof value === 'string' && referencePattern.test(value));
  const canonical = status >= 400 && data?.status === status && typeof bodyReference === 'string' && referencePattern.test(bodyReference) && typeof data?.code === 'string' && codePattern.test(data.code);
  const knownCode = [data?.code, data?.errorCode, data?.error, readHeader(response?.headers, 'X-Gateway-Error')]
    .find(value => typeof value === 'string' && codePattern.test(value));
  const [fallbackCode, statusMessage] = statusMessages[status] ?? (status >= 500
    ? ['INTERNAL_ERROR', 'Something went wrong. Your request could not be confirmed.']
    : ['REQUEST_REJECTED', 'The request could not be completed.']);
  const candidate = typeof data === 'string' ? data : data?.message ?? data?.detail ?? data?.details ?? data?.error;
  const legacyLoginCodes = {
    'Account is locked': 'ACCOUNT_LOCKED',
    'Account is temporarily locked due to multiple failed login attempts': 'ACCOUNT_LOCKED',
    'Account is disabled': 'ACCOUNT_UNAVAILABLE',
    'Your account has been deactivated. Please contact administration.': 'ACCOUNT_UNAVAILABLE',
    'Unable to verify student account status': 'ELIGIBILITY_UNAVAILABLE',
  };
  const legacyLoginCode = options.login && status === 400 && typeof candidate === 'string' && Object.hasOwn(legacyLoginCodes, candidate) ? legacyLoginCodes[candidate] : undefined;
  const code = knownCode ?? legacyLoginCode ?? (status ? fallbackCode : kind.toUpperCase());
  let message;
  if (cancelled) message = '';
  else if (options.login && network) message = 'The sign-in service could not be reached. Check your connection.';
  else if (options.login && status === 403 && !Object.hasOwn(codeMessages, code)) message = codeMessages.ACCOUNT_UNAVAILABLE;
  else if (options.login && status === 429) message = 'Too many sign-in attempts. Wait before trying again.';
  else if (options.login && status >= 500) message = 'Sign-in is temporarily unavailable. Try again shortly.';
  else if (options.login && (status === 401 || code === 'INVALID_CREDENTIALS' || (status === 400 && !canonical && !Object.hasOwn(codeMessages, code)))) message = codeMessages.INVALID_CREDENTIALS;
  else if (Object.hasOwn(codeMessages, code)) message = codeMessages[code];
  else if ((canonical && status < 500 && safeText(candidate)) || safeLegacyMessages.has(candidate)) message = candidate;
  else message = options.fallback || (timeout ? 'The request timed out. Its outcome could not be confirmed.'
    : network ? 'The server could not be reached. Check your connection.' : statusMessage);
  const fieldErrors = readFields(data, canonical);
  return { code, status, message, fieldErrors, requestId, kind,
    retryAfterSeconds: parseRetryAfter(readHeader(response?.headers, 'Retry-After')) };
}

/** Keep Axios errors and receipt bodies intact for existing callers. */
export function rememberApiError(error, sessionRetained = false) {
  if (error && typeof error === 'object') {
    const normalized = normalizeApiError(error);
    remembered.set(error, sessionRetained ? { ...normalized, code: 'SESSION_CHECK_UNAVAILABLE',
      message: 'This service could not verify the request. Your session is retained. Try again shortly.' } : normalized);
  }
  return error;
}

export function apiErrorMessage(error, fallback, options = {}) {
  return normalizeApiError(error, { ...options, fallback }).message;
}

export function focusFirstInvalidField(form, fieldErrors) {
  for (const name of Object.keys(fieldErrors)) {
    const control = form?.elements?.namedItem(name);
    if (typeof control?.focus === 'function' && !control.disabled) {
      control.focus();
      return true;
    }
  }
  return false;
}

export async function apiFailureFromResponse(response) {
  const error = new Error(`Request failed with status ${response.status}`);
  const data = await response.json().catch(() => undefined);
  error.response = { status: response.status, headers: response.headers, data };
  return rememberApiError(error);
}
